import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Input, Switch, Avatar } from 'antd';
import type { BudgetPeriod, IPracticeByProviderRow, SpecialtyOverrides } from 'types/marketing';
import s from './s.module.less';

// Re-exported for existing importers of this path.
export type { SpecialtyOverrides };

interface Props {
    providers: IPracticeByProviderRow[];
    baseBid: number;
    budgetCap: number;
    budgetPeriod: BudgetPeriod;
    budgetExcluded: Set<number>;
    initialOverrides?: SpecialtyOverrides;
    onChange?: (overrides: SpecialtyOverrides) => void;
    disabled?: boolean;
}

interface SpecGroup {
    key: string;
    name: string;
    icon: string;
    providers: IPracticeByProviderRow[];
}

const getInitials = (name: string) => {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    return (parts[0]?.[0] ?? '').toUpperCase();
};

const GroupSpecialtyOverrides: React.FC<Props> = ({
    providers,
    baseBid,
    budgetCap,
    budgetPeriod,
    budgetExcluded,
    initialOverrides,
    onChange,
    disabled = false,
}) => {
    const [panelOpen, setPanelOpen] = useState(false);
    const [expandedSpecs, setExpandedSpecs] = useState<Set<string>>(new Set());
    const [disabledSpecs, setDisabledSpecs] = useState<Set<string>>(new Set());
    const [specBids, setSpecBids] = useState<Record<string, number>>({});
    const [specCaps, setSpecCaps] = useState<Record<string, string>>({});
    const [provBids, setProvBids] = useState<Record<string, Record<number, number>>>({});
    const [provCaps, setProvCaps] = useState<Record<string, Record<number, string>>>({});
    const [disabledProvSpecs, setDisabledProvSpecs] = useState<Record<string, Set<number>>>({});
    const prevOverridesRef = useRef<SpecialtyOverrides | undefined>();
    const prevBaseBidRef = useRef<number | null>(null);
    const prevBudgetCapRef = useRef<number | null>(null);

    useEffect(() => {
        if (initialOverrides && initialOverrides !== prevOverridesRef.current) {
            prevOverridesRef.current = initialOverrides;
            setSpecBids(initialOverrides.specBids);
            setSpecCaps(initialOverrides.specCaps);
            setProvBids(initialOverrides.provBids);
            setProvCaps(initialOverrides.provCaps);
            setDisabledSpecs(initialOverrides.disabledSpecs);
            setDisabledProvSpecs(initialOverrides.disabledProvSpecs);
            prevBaseBidRef.current = baseBid;
            prevBudgetCapRef.current = budgetCap;
        }
    }, [initialOverrides, baseBid, budgetCap]);

    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    useEffect(() => {
        onChangeRef.current?.({ specBids, specCaps, provBids, provCaps, disabledSpecs, disabledProvSpecs });
    }, [specBids, specCaps, provBids, provCaps, disabledSpecs, disabledProvSpecs]);

    useEffect(() => {
        if (!prevOverridesRef.current) return;
        if (prevBaseBidRef.current === baseBid) return;
        const prevBid = prevBaseBidRef.current;
        prevBaseBidRef.current = baseBid;
        if (prevBid !== null && baseBid > prevBid) {
            setSpecBids((prev) => {
                if (!Object.keys(prev).length) return prev;
                let changed = false;
                const next: Record<string, number> = {};
                Object.entries(prev).forEach(([k, v]) => {
                    if (v < baseBid) {
                        next[k] = baseBid;
                        changed = true;
                    } else {
                        next[k] = v;
                    }
                });
                return changed ? next : prev;
            });
            setProvBids((prev) => {
                if (!Object.keys(prev).length) return prev;
                let changed = false;
                const next: Record<string, Record<number, number>> = {};
                Object.entries(prev).forEach(([specKey, provMap]) => {
                    const nextProv: Record<number, number> = {};
                    Object.entries(provMap).forEach(([id, v]) => {
                        if (v < baseBid) {
                            nextProv[Number(id)] = baseBid;
                            changed = true;
                        } else {
                            nextProv[Number(id)] = v;
                        }
                    });
                    next[specKey] = nextProv;
                });
                return changed ? next : prev;
            });
        }
    }, [baseBid]);

    useEffect(() => {
        if (!prevOverridesRef.current) return;
        if (prevBudgetCapRef.current === budgetCap) return;
        const prevCap = prevBudgetCapRef.current;
        prevBudgetCapRef.current = budgetCap;
        if (prevCap !== null && budgetCap < prevCap && budgetCap > 0) {
            setSpecCaps((prev) => {
                if (!Object.keys(prev).length) return prev;
                let changed = false;
                const next: Record<string, string> = {};
                Object.entries(prev).forEach(([k, v]) => {
                    const num = parseFloat(v);
                    if (Number.isFinite(num) && num > budgetCap) {
                        next[k] = String(budgetCap);
                        changed = true;
                    } else {
                        next[k] = v;
                    }
                });
                return changed ? next : prev;
            });
            setProvCaps((prev) => {
                if (!Object.keys(prev).length) return prev;
                let changed = false;
                const next: Record<string, Record<number, string>> = {};
                Object.entries(prev).forEach(([specKey, provMap]) => {
                    const nextProv: Record<number, string> = {};
                    Object.entries(provMap).forEach(([id, v]) => {
                        const num = parseFloat(v);
                        if (Number.isFinite(num) && num > budgetCap) {
                            nextProv[Number(id)] = String(budgetCap);
                            changed = true;
                        } else {
                            nextProv[Number(id)] = v;
                        }
                    });
                    next[specKey] = nextProv;
                });
                return changed ? next : prev;
            });
        }
    }, [budgetCap]);

    const capLabel = budgetPeriod === 'WEEKLY' ? 'Weekly cap' : 'Monthly cap';

    const specGroups: SpecGroup[] = useMemo(() => {
        const activeProviders = providers.filter((p) => !budgetExcluded.has(p.providerId));
        const map = new Map<string, { logo: string; providers: IPracticeByProviderRow[] }>();
        activeProviders.forEach((p) => {
            p.services
                ?.filter((svc) => svc.specialtyName)
                .forEach((svc) => {
                    const name = svc.specialtyName;
                    if (!map.has(name)) map.set(name, { logo: svc.logo, providers: [] });
                    const group = map.get(name)!;
                    if (!group.providers.some((existing) => existing.providerId === p.providerId)) {
                        group.providers.push(p);
                    }
                });
        });
        return Array.from(map.entries())
            .map(([name, { logo, providers: provs }]) => ({
                key: name.toLowerCase().replace(/\s+/g, '_'),
                name,
                icon: logo,
                providers: provs,
            }))
            .filter((g) => g.providers.length > 0);
    }, [providers, budgetExcluded]);

    const toggleExpand = (key: string) => {
        setExpandedSpecs((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const toggleSpec = (key: string, checked: boolean) => {
        setDisabledSpecs((prev) => {
            const next = new Set(prev);
            if (checked) {
                next.delete(key);
            } else {
                next.add(key);
                setExpandedSpecs((ep) => {
                    const ne = new Set(ep);
                    ne.delete(key);
                    return ne;
                });
            }
            return next;
        });
    };

    const [errors, setErrors] = useState<Record<string, string>>({});

    const errorTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

    const setError = (key: string, msg: string) => {
        setErrors((prev) => ({ ...prev, [key]: msg }));
        if (errorTimers.current[key]) clearTimeout(errorTimers.current[key]);
        errorTimers.current[key] = setTimeout(() => {
            setErrors((prev) => {
                if (!prev[key]) return prev;
                const next = { ...prev };
                delete next[key];
                return next;
            });
            delete errorTimers.current[key];
        }, 2500);
    };
    const clearError = (key: string) => {
        if (errorTimers.current[key]) {
            clearTimeout(errorTimers.current[key]);
            delete errorTimers.current[key];
        }
        setErrors((prev) => {
            if (!prev[key]) return prev;
            const next = { ...prev };
            delete next[key];
            return next;
        });
    };

    const budgetLabel = budgetPeriod === 'WEEKLY' ? 'weekly budget' : 'monthly budget';

    const getSpecBid = (name: string) => specBids[name] ?? baseBid;

    const handleSpecBidChange = (name: string, value: string) => {
        const num = parseFloat(value) || 0;
        setSpecBids((prev) => ({ ...prev, [name]: num }));
        clearError(`spec-bid:${name}`);
    };

    const handleSpecBidBlur = (name: string) => {
        let finalBid = specBids[name];
        if (finalBid == null) return;
        if (finalBid < baseBid) {
            finalBid = baseBid;
            setSpecBids((prev) => ({ ...prev, [name]: baseBid }));
            setError(`spec-bid:${name}`, `Can't be below $${baseBid.toLocaleString()} campaign bid`);
        }
        const key = name.toLowerCase().replace(/\s+/g, '_');
        setProvBids((prev) => {
            const provMap = prev[key];
            if (!provMap) return prev;
            let changed = false;
            const nextProv: Record<number, number> = {};
            Object.entries(provMap).forEach(([id, v]) => {
                if (v < finalBid!) {
                    nextProv[Number(id)] = finalBid!;
                    changed = true;
                } else {
                    nextProv[Number(id)] = v;
                }
            });
            return changed ? { ...prev, [key]: nextProv } : prev;
        });
    };

    const handleSpecCapChange = (name: string, value: string) => {
        setSpecCaps((prev) => ({ ...prev, [name]: value }));
        clearError(`spec-cap:${name}`);
    };

    const handleSpecCapBlur = (name: string) => {
        let finalCap = parseFloat(specCaps[name]);
        if (!Number.isFinite(finalCap) || !finalCap) return;
        if (budgetCap > 0 && finalCap > budgetCap) {
            finalCap = budgetCap;
            setSpecCaps((prev) => ({ ...prev, [name]: String(budgetCap) }));
            setError(`spec-cap:${name}`, `Can't exceed $${budgetCap.toLocaleString()} ${budgetLabel}`);
        }
        const key = name.toLowerCase().replace(/\s+/g, '_');
        setProvCaps((prev) => {
            const provMap = prev[key];
            if (!provMap) return prev;
            let changed = false;
            const nextProv: Record<number, string> = {};
            Object.entries(provMap).forEach(([id, v]) => {
                const num = parseFloat(v);
                if (Number.isFinite(num) && num > finalCap) {
                    nextProv[Number(id)] = String(finalCap);
                    changed = true;
                } else {
                    nextProv[Number(id)] = v;
                }
            });
            return changed ? { ...prev, [key]: nextProv } : prev;
        });
    };

    const handleQuickPreset = (name: string, offset: number) => {
        const current = getSpecBid(name);
        const newVal = Math.max(baseBid, current + offset);
        setSpecBids((prev) => ({ ...prev, [name]: newVal }));
        clearError(`spec-bid:${name}`);
    };

    const handleProvBidChange = (specKey: string, id: number, value: string) => {
        const num = parseFloat(value) || 0;
        setProvBids((prev) => ({ ...prev, [specKey]: { ...prev[specKey], [id]: num } }));
        clearError(`prov-bid:${specKey}:${id}`);
    };

    const handleProvBidBlur = (specKey: string, specName: string, id: number) => {
        const val = provBids[specKey]?.[id];
        if (val == null) return;
        const floor = getSpecBid(specName);
        if (val < floor) {
            setProvBids((prev) => ({ ...prev, [specKey]: { ...prev[specKey], [id]: floor } }));
            setError(`prov-bid:${specKey}:${id}`, `Can't be below $${floor.toLocaleString()} specialty bid`);
        }
    };

    const handleProvCapChange = (specKey: string, id: number, value: string) => {
        setProvCaps((prev) => ({ ...prev, [specKey]: { ...prev[specKey], [id]: value } }));
        clearError(`prov-cap:${specKey}:${id}`);
    };

    const handleProvCapBlur = (specKey: string, specName: string, id: number) => {
        const val = parseFloat(provCaps[specKey]?.[id]);
        if (!Number.isFinite(val) || !val) return;
        const specCapVal = parseFloat(specCaps[specName]);
        const ceiling = Number.isFinite(specCapVal) && specCapVal > 0 ? specCapVal : budgetCap;
        const ceilingLabel =
            Number.isFinite(specCapVal) && specCapVal > 0
                ? `$${specCapVal.toLocaleString()} specialty cap`
                : `$${budgetCap.toLocaleString()} ${budgetLabel}`;
        if (ceiling > 0 && val > ceiling) {
            setProvCaps((prev) => ({ ...prev, [specKey]: { ...prev[specKey], [id]: String(ceiling) } }));
            setError(`prov-cap:${specKey}:${id}`, `Can't exceed ${ceilingLabel}`);
        }
    };

    const toggleProvInSpec = (specKey: string, providerId: number) => {
        setDisabledProvSpecs((prev) => {
            const specSet = new Set(prev[specKey] || []);
            if (specSet.has(providerId)) {
                specSet.delete(providerId);
            } else {
                specSet.add(providerId);
            }
            return { ...prev, [specKey]: specSet };
        });
    };

    const renderProviderRow = (p: IPracticeByProviderRow, specName: string, specKey: string) => {
        const isOff = disabledProvSpecs[specKey]?.has(p.providerId) || false;
        const bid = provBids[specKey]?.[p.providerId] ?? getSpecBid(specName);
        const bidErrKey = `prov-bid:${specKey}:${p.providerId}`;
        const capErrKey = `prov-cap:${specKey}:${p.providerId}`;

        return (
            <div key={p.providerId} className={s.provRow}>
                {p.avatar ? (
                    <Avatar src={p.avatar} size={32} />
                ) : (
                    <Avatar size={32} className={s.provAvatar}>
                        {getInitials(p.name)}
                    </Avatar>
                )}
                <div className={s.provInfo}>
                    <div className={s.provName}>{p.name}</div>
                    <div className={s.provMeta}> {p.services?.map((svc) => svc.specialtyName).join(', ')}</div>
                </div>
                <div className={s.provField}>
                    <span className={s.provFieldLabel}>Bid / booking</span>
                    <Input
                        prefix="$"
                        value={bid}
                        onChange={(e) => handleProvBidChange(specKey, p.providerId, e.target.value)}
                        onBlur={() => handleProvBidBlur(specKey, specName, p.providerId)}
                        className={`${s.provInput} ${errors[bidErrKey] ? s.inputError : ''}`}
                        type="number"
                        disabled={disabled || isOff}
                    />
                    {errors[bidErrKey] && <div className={s.errorTip}>{errors[bidErrKey]}</div>}
                </div>
                <div className={s.provField}>
                    <span className={s.provFieldLabel}>{capLabel}</span>
                    <Input
                        prefix="$"
                        placeholder="Auto"
                        value={provCaps[specKey]?.[p.providerId] ?? ''}
                        onChange={(e) => handleProvCapChange(specKey, p.providerId, e.target.value)}
                        onBlur={() => handleProvCapBlur(specKey, specName, p.providerId)}
                        className={`${s.provInput} ${errors[capErrKey] ? s.inputError : ''}`}
                        type="number"
                        disabled={disabled || isOff}
                    />
                    {errors[capErrKey] && <div className={s.errorTip}>{errors[capErrKey]}</div>}
                </div>
                <Switch
                    size="small"
                    checked={!isOff}
                    onChange={() => toggleProvInSpec(specKey, p.providerId)}
                    disabled={disabled}
                />
            </div>
        );
    };

    const renderSpecCard = (group: SpecGroup) => {
        const isSpecOff = disabledSpecs.has(group.key);
        const isExpanded = expandedSpecs.has(group.key);
        const activeCount = group.providers.filter((p) => !disabledProvSpecs[group.key]?.has(p.providerId)).length;
        const totalCount = group.providers.length;
        const bid = getSpecBid(group.name);

        return (
            <div key={group.key} className={`${s.specCard} ${isExpanded ? s.specCardExpanded : ''}`}>
                <div className={s.specMain} onClick={() => toggleExpand(group.key)}>
                    <img src={group.icon} alt={group.name} className={s.specIcon} />
                    <div className={s.specInfo}>
                        <div className={s.specName}>{group.name}</div>
                        <div className={`${s.specMeta} ${isSpecOff ? s.specMetaOff : ''}`}>
                            {isSpecOff
                                ? 'Specialty off — bidding $0'
                                : `${activeCount} of ${totalCount} provider${totalCount === 1 ? '' : 's'} on Pro-Listing`}
                        </div>
                    </div>
                    <div className={s.specField} onClick={(e) => e.stopPropagation()}>
                        <span className={s.specFieldLabel}>Bid / booking</span>
                        <Input
                            prefix="$"
                            value={bid}
                            onChange={(e) => handleSpecBidChange(group.name, e.target.value)}
                            onBlur={() => handleSpecBidBlur(group.name)}
                            className={`${s.specInput} ${errors[`spec-bid:${group.name}`] ? s.inputError : ''}`}
                            type="number"
                            disabled={disabled || isSpecOff}
                        />
                        {errors[`spec-bid:${group.name}`] && (
                            <div className={s.errorTip}>{errors[`spec-bid:${group.name}`]}</div>
                        )}
                    </div>
                    <div className={s.specField} onClick={(e) => e.stopPropagation()}>
                        <span className={s.specFieldLabel}>{capLabel}</span>
                        <Input
                            prefix="$"
                            placeholder="Auto"
                            value={specCaps[group.name] ?? ''}
                            onChange={(e) => handleSpecCapChange(group.name, e.target.value)}
                            onBlur={() => handleSpecCapBlur(group.name)}
                            className={`${s.specCapInput} ${errors[`spec-cap:${group.name}`] ? s.inputError : ''}`}
                            type="number"
                            disabled={disabled || isSpecOff}
                        />
                        {errors[`spec-cap:${group.name}`] && (
                            <div className={s.errorTip}>{errors[`spec-cap:${group.name}`]}</div>
                        )}
                    </div>
                    <Switch
                        size="small"
                        checked={!isSpecOff}
                        onChange={(checked) => toggleSpec(group.key, checked)}
                        disabled={disabled}
                        onClick={(_, e) => e.stopPropagation()}
                    />
                    <button
                        type="button"
                        className={`${s.specExpandBtn} ${isExpanded ? s.specExpandBtnOpen : ''}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            toggleExpand(group.key);
                        }}
                        disabled={disabled || isSpecOff}
                    >
                        <svg
                            width="12"
                            height="12"
                            viewBox="0 0 12 12"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.6"
                        >
                            <path d="M2 4.5L6 8.5L10 4.5" />
                        </svg>
                    </button>
                </div>

                {isExpanded && !isSpecOff && (
                    <div className={s.specExpanded}>
                        <div className={s.quickLabel}>
                            Quick presets — adjust this specialty&apos;s bid relative to the base
                        </div>
                        <div className={s.quickChips}>
                            <button
                                type="button"
                                className={s.quickChip}
                                onClick={() => handleQuickPreset(group.name, 10)}
                                disabled={disabled}
                            >
                                +$10
                            </button>
                            <button
                                type="button"
                                className={s.quickChip}
                                onClick={() => handleQuickPreset(group.name, 20)}
                                disabled={disabled}
                            >
                                +$20
                            </button>
                            <button
                                type="button"
                                className={s.quickChip}
                                onClick={() => handleQuickPreset(group.name, 30)}
                                disabled={disabled}
                            >
                                +$30
                            </button>
                        </div>
                        <div className={s.provHeader}>
                            <div className={s.provHeaderTitle}>Override per-provider bids</div>
                            <div className={s.provHeaderSub}>
                                Set a custom bid and {capLabel.toLowerCase()} for each provider in this specialty.
                                Defaults inherit the specialty bid above.
                            </div>
                        </div>
                        <div className={s.provList}>
                            {group.providers.map((p) => renderProviderRow(p, group.name, group.key))}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className={s.container}>
            <div className={s.header} onClick={() => setPanelOpen(!panelOpen)}>
                <div className={s.headerLeft}>
                    <svg
                        width="14"
                        height="14"
                        viewBox="0 0 16 16"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        className={`${s.headerChevron} ${panelOpen ? s.headerChevronOpen : ''}`}
                    >
                        <polyline points="6 4 10 8 6 12" />
                    </svg>
                    <span className={s.headerTitle}>Override bids by specialty or provider</span>
                </div>
            </div>

            {panelOpen && (
                <>
                    <div className={s.headerDesc}>
                        Set a bid per specialty, then expand any row to give specific providers a boost or per-provider{' '}
                        {capLabel.toLowerCase()}.
                    </div>
                    {specGroups.length === 0 ? (
                        <div className={s.empty}>No providers in your campaign cohort yet.</div>
                    ) : (
                        <div className={s.specList}>{specGroups.map((g) => renderSpecCard(g))}</div>
                    )}
                </>
            )}
        </div>
    );
};

export default GroupSpecialtyOverrides;
